import type { Veiculo } from './entregador';
import type { CodigoErro } from './erros';
import type { RegraDePagamento } from './ganhos';

// Tipos do domínio, independentes do banco (o mapeamento linha → domínio fica em @entregas/api).
// Datas trafegam como string ISO 8601; dinheiro sempre em centavos.

export const STATUS_PEDIDO = [
  'pendente',
  'a_caminho',
  'entregue',
  'nao_entregue',
  'cancelado',
] as const;
export type StatusPedido = (typeof STATUS_PEDIDO)[number];

export const FORMAS_PAGAMENTO = [
  'pix',
  'dinheiro',
  'credito',
  'debito',
  'vale',
  'fiado',
  'pago',
] as const;
export type FormaPagamento = (typeof FORMAS_PAGAMENTO)[number];

/** Uma parte do pagamento. Pedido tem 1 ou 2 partes, que somam o total. */
export interface ParteDoPagamento {
  forma: FormaPagamento;
  valorCentavos: number;
  /** Só no crédito (1 a 12). */
  parcelas: number | null;
}

export const PAPEIS_MEMBRO = ['dono', 'balcao'] as const;
export type PapelMembro = (typeof PAPEIS_MEMBRO)[number];

export interface Coordenada {
  lat: number;
  lng: number;
}

/**
 * De onde veio o ponto de um endereço: o celular do entregador achou pelo texto ('geocodificado') ou
 * gravou onde entregou ('porta'). A porta prevalece (o banco não troca uma porta por um ponto achado).
 */
export const ORIGENS_DO_LOCAL = ['geocodificado', 'porta'] as const;
export type OrigemDoLocal = (typeof ORIGENS_DO_LOCAL)[number];

/** O GPS do "Entreguei" só vira o ponto da porta com até esta precisão (metros); o banco recusa acima. */
export const PRECISAO_MAXIMA_DA_PORTA_M = 30;
/** Depois disto da entrega o banco não aceita mais a porta (`porta_vencida`). */
export const VALIDADE_DA_PORTA_MS = 12 * 60 * 60 * 1000;
/**
 * Ponto do endereço a mais disto da loja é outra cidade com a mesma rua: o banco recusa com
 * `local_longe_da_loja` quando a loja marcou o local (metros).
 */
export const LONGE_DA_LOJA_M = 50_000;
/**
 * O ponto achado pelo texto ('geocodificado') vale só até esta distância da loja (metros); a porta
 * continua até LONGE_DA_LOJA_M. Acima, o banco recusa com `local_longe_da_loja`.
 */
export const LONGE_DA_LOJA_ACHADO_M = 20_000;
/** Porta a menos disto do local da loja é o "Entreguei" tocado na loja: `porta_na_loja` (metros). */
export const PORTA_PERTO_DA_LOJA_M = 150;
/**
 * Porta a mais disto do ponto que o pedido ou o cadastro já têm: `porta_longe_do_destino` (metros).
 * De um ponto achado pelo texto passa só com precisão de até PRECISAO_DA_PORTA_CONTRA_ACHADO_M; de uma
 * porta anterior ou de um ponto marcado à mão, nunca.
 */
export const PORTA_LONGE_DO_DESTINO_M = 300;
export const PRECISAO_DA_PORTA_CONTRA_ACHADO_M = 15;

/**
 * Pedido a caminho há mais disto não conta mais como rota: o banco não grava posição (e apaga a última),
 * e o link do cliente não mostra onde o entregador está.
 */
export const PRAZO_DA_SAIDA_HORAS = 6;
/** Pontos do GPS que o banco descarta: hora mais adiantada que isto... */
export const PONTO_NO_FUTURO_MAX_MS = 2 * 60_000;
/** ...ou mais velha que isto... */
export const PONTO_VELHO_MAX_MS = 15 * 60_000;
/** ...ou precisão pior que esta (metros). */
export const PRECISAO_MAXIMA_DA_POSICAO_M = 150;
/** O link do cliente mostra a posição com hora do GPS até isto atrás. */
export const POSICAO_RECENTE_NO_LINK_MS = 10 * 60_000;
/** O dono vê no mapa só posições com hora do GPS até isto atrás. */
export const POSICAO_RECENTE_PARA_O_DONO_MS = 30 * 60_000;
/** Precisão acima desta não marca o local da loja: o banco recusa com `local_impreciso` (metros). */
export const PRECISAO_MAXIMA_DA_LOJA_M = 500;
/** Entregas numa saída só ("Sair com N"): acima disto o banco recusa com `saida_grande_demais`. */
export const MAX_ENTREGAS_POR_SAIDA = 20;

/** Onde a loja fica, marcado com o GPS de um aparelho dentro dela (app do dono ou painel). */
export interface LocalDaLoja extends Coordenada {
  /** Precisão que o aparelho informou ao marcar, em metros. */
  precisaoM: number | null;
  marcadoEm: string;
}

export interface Empresa {
  id: string;
  nome: string;
  telefone: string | null;
  viradaDiaHora: number;
  fuso: string;
  /** Pré-preenche a cidade dos endereços novos. */
  cidade: string | null;
  uf: string | null;
  taxaEntregaPadraoCentavos: number;
  /** null = a loja ainda não marcou onde fica (a rota do entregador fica sem distâncias da loja). */
  local: LocalDaLoja | null;
  /** Prazo combinado com o cliente (10 a 240 min): a régua do "vence em / atrasou" da lista. */
  prazoMinutos: number;
  /** Pedido com produtos a partir disto sai sem taxa (só sugestão no Novo pedido); null = nunca. */
  entregaGratisAPartirCentavos: number | null;
  /** Imagem da loja em data URL (PNG, WebP ou JPEG, até 512 px); null = sem logo (as iniciais no lugar). */
  logo: string | null;
}

/** Telefone e local da loja que chegam ao app do entregador com cada cadastro (minhas_lojas). */
export interface ContatoDaLoja {
  /** Só dígitos com DDI; null = a loja não informou ("Falar com a loja" some). */
  empresaTelefone: string | null;
  /** null = a loja ainda não marcou onde fica. */
  empresaLocal: Coordenada | null;
}

export interface Cliente {
  id: string;
  empresaId: string;
  nome: string;
  /** Só dígitos, com DDI: 5527999990000. */
  telefone: string;
  observacao: string | null;
  criadoEm: string;
}

/** Cliente com os números da ficha: quantos pedidos, último pedido, ticket médio. */
export interface ResumoCliente extends Cliente {
  totalPedidos: number;
  pedidosEntregues: number;
  ultimoPedidoEm: string | null;
  ticketMedioCentavos: number;
}

export interface Endereco {
  id: string;
  clienteId: string;
  rotulo: string | null;
  logradouro: string;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  referencia: string | null;
  coordenada: Coordenada | null;
  principal: boolean;
}

export interface Entregador {
  id: string;
  empresaId: string;
  nome: string;
  telefone: string | null;
  ativo: boolean;
  /** Código fixo de 6 dígitos que o entregador digita no app. */
  codigo: string;
  /** Há um aparelho conectado com este código. */
  pareado: boolean;
  /** Quando a loja desativou (null = ativo ou desativado antes de guardarmos a data). */
  desativadoEm: string | null;
  /** Como a loja paga este entregador. */
  regra: RegraDePagamento;
}

export interface Pedido {
  id: string;
  empresaId: string;
  numero: number;
  clienteId: string;
  enderecoId: string | null;
  /** Endereço fotografado no momento do pedido (não muda se o cadastro mudar). */
  enderecoTexto: string;
  destino: Coordenada | null;
  itens: string;
  valorCentavos: number;
  taxaEntregaCentavos: number;
  /** Forma da 1ª parte (resumo para listas). O detalhe está em `pagamentos`. */
  formaPagamento: FormaPagamento;
  pagamentos: ParteDoPagamento[];
  /** Nota que o cliente vai usar para a parte em dinheiro. */
  trocoParaCentavos: number | null;
  observacao: string | null;
  status: StatusPedido;
  entregadorId: string | null;
  motivo: string | null;
  tokenRastreio: string;
  criadoEm: string;
  saiuEm: string | null;
  entregueEm: string | null;
  /** Quando terminou (entregue OU não entregue). `entregueEm` só existe para entregue. */
  concluidoEm: string | null;
}

/** Pedido como o app do entregador recebe (já com nome e telefone do cliente). */
export interface EntregaDoEntregador
  extends Omit<Pedido, 'empresaId' | 'clienteId' | 'enderecoId'> {
  clienteNome: string;
  clienteTelefone: string;
  /**
   * Por que não entregou (status nao_entregue). Pedido que a loja reenviou (de novo para sair) continua
   * com o motivo da tentativa anterior até sair outra vez.
   */
  motivo: string | null;
  /** De onde veio o ponto do destino; null = sem ponto, ou marcado à mão no painel. */
  destinoOrigem: OrigemDoLocal | null;
}

/** Resultado de cada pedido em "Sair com N" (iniciar_saida): 'ok' ou o motivo da recusa. */
export interface SaidaDoPedido {
  pedidoId: string;
  /** Ex.: 'pedido_de_outro_entregador' (outro entregador já saiu com ele). */
  resultado: 'ok' | CodigoErro;
}

/** O que a loja vê do perfil que o entregador preencheu no app (só dos entregadores dela, ativos). */
export interface PerfilDoEntregadorNaLoja {
  entregadorId: string;
  /** WhatsApp que a pessoa informou no app (só dígitos com DDI), ao lado do que a loja cadastrou. */
  telefone: string | null;
  veiculo: Veiculo | null;
  modelo: string | null;
  placa: string | null;
  cor: string | null;
  levaBotijao: boolean;
}

export interface PosicaoEntregador extends Coordenada {
  entregadorId: string;
  precisao: number | null;
  atualizadoEm: string;
}

/** O que o link público mostra: sem dados pessoais além do primeiro nome. */
export interface Rastreio {
  /** Hora do servidor na leitura (o mapa conta o tempo por ela); null antes da migration que a manda. */
  agora: string | null;
  /** `temLogo`: a imagem vem à parte (obterLogoDoRastreio), uma vez, e não a cada releitura. */
  loja: { nome: string; telefone: string | null; temLogo: boolean };
  pedido: {
    numero: number;
    status: StatusPedido;
    criadoEm: string;
    saiuEm: string | null;
    entregueEm: string | null;
    clientePrimeiroNome: string;
    destino: Coordenada | null;
    /** O que foi pedido e o total (a tela "Recebeu tudo certinho?" mostra). */
    itens: string;
    totalCentavos: number;
    /** O cliente tocou em "Sim, recebi meu pedido". */
    clienteConfirmouEm: string | null;
    /** Entregue há menos de 48 h: ainda dá para confirmar e avaliar. */
    podeAvaliar: boolean;
  };
  /** A avaliação que o cliente já mandou (uma por pedido). */
  avaliacao: AvaliacaoDoCliente | null;
  entregador: {
    primeiroNome: string;
    posicao: (Coordenada & { atualizadoEm: string }) | null;
  } | null;
}

export interface AvaliacaoDoCliente {
  nota: number;
  /** O que o cliente marcou ("Chegou rápido", "Atrasou"…). */
  marcas: string[];
  comentario: string | null;
}
